/**
 * 운영자 지표 화면의 「ChatGPT | Gemini」 탭 (261104-gemini-concurrent-lane — Gemini 는 어드민 전용).
 *
 * - 탭은 **주소의 `?engine=gemini`** 가 정한다 — 새로고침·링크 공유에도 유지된다. ChatGPT 면 주소에 붙이지 않는다
 * - 화면은 `useEngine()` 의 값을 지표 요청의 `engine` 으로 넘긴다. 서버는 생략을 ChatGPT 로 읽는다
 * - 다른 검색 값(실제|테스트 탭 등)은 유지하고 쪽 번호만 버린다 — 엔진이 바뀌면 목록이 달라진다
 * - ★ 고객 화면(`/app`)에는 달지 않는다. 서버도 운영자가 아니면 Gemini 요청을 403 으로 막는다
 */
import type { Engine } from '@geo-admin/shared/contract/probe-values.ts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Tabs } from './BrandKindTabs.tsx';

const TABS: readonly { id: Engine; label: string }[] = [
  { id: 'chatgpt-web', label: 'ChatGPT' },
  { id: 'gemini-web', label: 'Gemini' },
];

/** 주소 값 — 짧게 쓴다(`?engine=gemini`) */
const SEARCH_VALUE: Record<Engine, string | undefined> = { 'chatgpt-web': undefined, 'gemini-web': 'gemini' };

export function useEngine(): Engine {
  const { engine } = useSearch({ strict: false }) as { engine?: string };
  return engine === 'gemini' ? 'gemini-web' : 'chatgpt-web';
}

/** 다른 화면으로 넘어가는 링크에 엔진을 실어 보낸다 — 개요에서 Gemini 질문을 누르면 상세도 Gemini 로 열린다 */
export function engineSearch(e: Engine): { engine?: string } {
  const v = SEARCH_VALUE[e];
  return v ? { engine: v } : {};
}

/** 화면 제목 옆 표기 — 「언급률」 같은 숫자가 어느 엔진 것인지 탭 밖에서도 보이게 */
export function engineLabel(e: Engine): string {
  return TABS.find((t) => t.id === e)?.label ?? e;
}

export function EngineTabs({ className }: { className?: string }) {
  const engine = useEngine();
  const navigate = useNavigate();
  const pick = (e: Engine) => {
    if (e === engine) return;
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => {
        const { engine: _engine, page: _page, ...rest } = prev;
        const v = SEARCH_VALUE[e];
        return v ? { ...rest, engine: v } : rest;
      }) as never,
    });
  };
  return <Tabs tabs={TABS} value={engine} onChange={pick} className={className} />;
}
